import { Typography } from '@euler/app/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  CoachingCustomerMood,
  CoachingPracticeCustomer,
} from '@euler/model/coaching-practice';
import { memo } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { aiColors } from '../../../../../ai-suite/theme';
import { Chip } from '../../../../components/Chip';
import { SubtitledHeader } from '../../../../components/SubtitledHeader';
import { kMoodColors, kMoodLabels, kStageLabels } from '../../../practiceCopy';
import { CustomerAvatar } from '../dialogue/CustomerAvatar';

/**
 * Who the trainee is talking to, how far along, the customer's mood
 * (practice mode) and the way out (结束 / 交卷 / 挂断) while there is one.
 */
export const PracticeHeader = memo(
  ({
    customer,
    progress,
    mood,
    stopLabel,
    onStop,
    onBack,
  }: {
    customer: CoachingPracticeCustomer;
    progress: string;
    /** Null in an exam. */
    mood: CoachingCustomerMood | null;
    stopLabel: string;
    /** Null once it has ended: nothing to stop. */
    onStop: (() => void) | null;
    onBack: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <SubtitledHeader
        onBack={onBack}
        leading={<CustomerAvatar size={rpx(36)} />}
        title={
          <Typography.Text
            size={rpx(14)}
            color={aiColors.foreground}
            numberOfLines={1}
          >
            <Typography.Text size={rpx(14)} bold color={aiColors.foreground}>
              {customer.name}
            </Typography.Text>
            {` · ${kStageLabels[customer.relationshipStage]}`}
          </Typography.Text>
        }
        subtitle={progress}
        trailing={
          // One row, its middle the button's: the chip lines up with it.
          <View
            style={{ flexDirection: 'row', alignItems: 'center', gap: rpx(8) }}
          >
            {mood ? (
              <View>
                <Chip text={kMoodLabels[mood]} color={kMoodColors[mood]} />
              </View>
            ) : null}
            {onStop ? (
              <TouchableOpacity
                accessibilityRole="button"
                onPress={onStop}
                style={{
                  height: rpx(32),
                  justifyContent: 'center',
                  paddingHorizontal: rpx(12),
                  borderRadius: rpx(10),
                  borderWidth: 1,
                  borderColor: aiColors.border,
                  backgroundColor: aiColors.card,
                }}
              >
                <Typography.Text size={rpx(12)} color={aiColors.foreground}>
                  {stopLabel}
                </Typography.Text>
              </TouchableOpacity>
            ) : null}
          </View>
        }
      />
    );
  },
);
